import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import message from '../../utils/message';
import { getUserSectionStats } from '../../reduxStore/actions/recordActionsHelper';
import { getUserTestReports } from '../../reduxStore/actions/testReportActionsHelper';
import { createExamination } from '../../reduxStore/actions/examinationActionsHelper';
import { userSectionStats$ } from '../../reduxStore/selectors/recordSelectors';
import { usersData$ } from '../../reduxStore/selectors/userSelectors';
import { latestTestReport$ } from '../../reduxStore/selectors/testReportSelectors';
import {
    selectExaminationCreating,
    selectExaminationError,
    selectCurrentExamination
} from '../../reduxStore/selectors/examinationSelector';
import sectionType from '../../utils/Enum/sectionType';
import AccuracyProgressBar from '../AccuracyProgressBar';
import TestReportModal from '../TestReportModal/TestReportModal';
import { generateMockTestReport } from '../../utils/mockTestReportData';
import styles from './asset/SATFullTestSelectorBox.module.scss';

const SATFullTestSelectorBox = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUser = useSelector(usersData$);
    const userSectionStats = useSelector(userSectionStats$);
    const latestReport = useSelector(latestTestReport$);
    const isCreatingExamination = useSelector(selectExaminationCreating);
    const examinationError = useSelector(selectExaminationError);
    const currentExamination = useSelector(selectCurrentExamination);

    const [isReportModalVisible, setIsReportModalVisible] = useState(false);
    const hasNavigatedRef = useRef(false);

    // Fetch user statistics and test reports when component mounts
    useEffect(() => {
        if (currentUser?.id) {
            dispatch(getUserSectionStats.request({ userId: currentUser.id }));
            dispatch(getUserTestReports({ userId: currentUser.id }));
        }
    }, [dispatch, currentUser?.id]);

    // Handle examination errors (simplified)
    useEffect(() => {
        if (examinationError) {
            message.error('Failed to start full test. Please try again.');
        }
    }, [examinationError]);

    // Handle successful examination creation and navigate (simplified)
    useEffect(() => {

        const examinationId = currentExamination?._id;

        // Only navigate once when examination is successfully created
        if (examinationId &&
            !isCreatingExamination &&
            !examinationError &&
            !hasNavigatedRef.current) {

            console.log('🧭 Navigating to examination:', examinationId);
            hasNavigatedRef.current = true;

            // Use setTimeout to avoid any potential synchronous issues
            setTimeout(() => {
                navigate(`/user-full-test/${examinationId}`);
            }, 100);
        }
    }, [currentExamination, isCreatingExamination, examinationError, navigate]);

    // Reset navigation flag when examination is cleared (e.g., when user navigates back)
    useEffect(() => {
        if (!currentExamination) {
            hasNavigatedRef.current = false;
        }
    }, [currentExamination]);

    // Get accuracy color based on percentage
    const getAccuracyColor = (accuracy) => {
        if (accuracy === 0) return '#9ca3af'; // Grey for no practice (0%)
        if (accuracy >= 80) return '#22c55e'; // Green for excellent (80-100%)
        if (accuracy >= 60) return '#eab308'; // Yellow for good (60-79%)
        return '#ef4444'; // Red for needs improvement (1-59%)
    };

    // Calculate overall SAT accuracy (all sections combined)
    const getOverallSATStats = () => {
        if (!userSectionStats) {
            return { accuracy: 0, date: '---', color: getAccuracyColor(0), totalQuestions: 108 };
        }

        // Reading & Writing sections
        const infoIdeas = userSectionStats[sectionType.INFORMATION_AND_IDEAS]?.accuracy || 0;
        const craftStructure = userSectionStats[sectionType.CRAFT_AND_STRUCTURE]?.accuracy || 0;
        const expressionIdeas = userSectionStats[sectionType.EXPRESSION_OF_IDEAS]?.accuracy || 0;
        const englishConventions = userSectionStats[sectionType.STANDARD_ENGLISH_CONVENTIONS]?.accuracy || 0;

        // Math sections
        const algebra = userSectionStats[sectionType.ALGEBRA]?.accuracy || 0;
        const advancedMath = userSectionStats[sectionType.ADVANCED_MATH]?.accuracy || 0;
        const problemSolving = userSectionStats[sectionType.PROBLEM_SOLVING_AND_DATA_ANALYSIS]?.accuracy || 0;
        const geometry = userSectionStats[sectionType.GEOMETRY_AND_TRIGONOMETRY]?.accuracy || 0;

        // Average accuracy across all sections
        const allSections = [infoIdeas, craftStructure, expressionIdeas, englishConventions, algebra, advancedMath, problemSolving, geometry];
        const practisedSections = allSections.filter(acc => acc > 0);
        const accuracy = practisedSections.length > 0
            ? Math.round(practisedSections.reduce((sum, acc) => sum + acc, 0) / practisedSections.length)
            : 0;

        // Get most recent practice date from all sections
        const allDates = [
            userSectionStats[sectionType.INFORMATION_AND_IDEAS]?.lastPracticeDate,
            userSectionStats[sectionType.CRAFT_AND_STRUCTURE]?.lastPracticeDate,
            userSectionStats[sectionType.EXPRESSION_OF_IDEAS]?.lastPracticeDate,
            userSectionStats[sectionType.STANDARD_ENGLISH_CONVENTIONS]?.lastPracticeDate,
            userSectionStats[sectionType.ALGEBRA]?.lastPracticeDate,
            userSectionStats[sectionType.ADVANCED_MATH]?.lastPracticeDate,
            userSectionStats[sectionType.PROBLEM_SOLVING_AND_DATA_ANALYSIS]?.lastPracticeDate,
            userSectionStats[sectionType.GEOMETRY_AND_TRIGONOMETRY]?.lastPracticeDate
        ].filter(date => date && date !== '---');

        let latestDate = '---';
        if (allDates.length > 0) {
            latestDate = allDates.reduce((latest, current) => {
                return new Date(current) > new Date(latest) ? current : latest;
            });
        }

        return { accuracy, date: latestDate, color: getAccuracyColor(accuracy), totalQuestions: 108 }; // SAT has 108 questions total
    };

    // Reset navigation flag when starting new examination
    const handleFullTestClick = () => {
        if (!currentUser?.id) {
            message.error('Please log in to start the full test');
            return;
        }

        console.log('🚀 Start Test button clicked');
        console.log('👤 Current user data:', { id: currentUser.id, type: typeof currentUser.id });

        // Reset navigation flag for new examination
        hasNavigatedRef.current = false;

        // Define minimal examination data to test performance
        const examinationData = {
            title: 'SAT Full Practice Test',
            description: 'Complete SAT practice test with Reading & Writing and Math sections',
            sectionConfigs: [
                {
                    sectionType: sectionType.PRACTICE_ALL_READING_WRITING,
                    totalQuestions: 64,
                    timeLimit: 64
                },
                {
                    sectionType: sectionType.PRACTICE_ALL_MATH,
                    totalQuestions: 44,
                    timeLimit: 70
                }
            ]
        };

        console.log('📦 Dispatching createExamination with data:', examinationData);
        console.log('🔧 User ID format check:', {
            userId: currentUser.id,
            isUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(currentUser.id),
            length: currentUser.id.length
        });

        // Dispatch Redux action to create examination
        dispatch(createExamination.request(examinationData));
    };

    const handleViewReportClick = () => {
        setIsReportModalVisible(true);
    };

    // For testing purposes - use mock data if no real report exists
    const reportToShow = latestReport || (currentUser?.id ? generateMockTestReport(currentUser.id) : null);

    const handleCloseReportModal = () => {
        setIsReportModalVisible(false);
    };

    const overallStats = getOverallSATStats();

    return (
        <div className={styles.tableSatPractice}>
            <div className={styles.satFullTestPracticeParent}>
                <div className={styles.satFullTestTitle}>SAT Full Practice Test</div>
                <div className={styles.frameParent}>
                    <div className={styles.frameGroup}>
                        <svg className={styles.warningCircleWarning} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className={styles.accuracy}>Overall Accuracy</div>
                    </div>
                    <div className={styles.calendarCalendarCheckParent}>
                        <svg className={styles.warningCircleWarning} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 14l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className={styles.accuracy}>Last Practice</div>
                    </div>
                    <div className={styles.questionCountParent}>
                        <svg className={styles.warningCircleWarning} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className={styles.accuracy}>Total Questions</div>
                    </div>
                </div>
            </div>
            <div className={styles.tableSatPracticeChild} />

            {/* Full Test Overview Section */}
            <div className={styles.frameContainer}>
                <div className={styles.frameDiv}>
                    <div className={styles.tagSatPracticeParent}>
                        <div className={styles.tagSatPractice}>
                            <svg className={styles.tagSatPracticeChild} width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div className={styles.informationAndIdeasParent}>
                                <div className={styles.informationAndIdeas}>Complete SAT Practice Test</div>
                                <div className={styles.explanationSentence}>Reading & Writing + Math sections (approx. 2 hours 14 minutes)</div>
                            </div>
                        </div>
                        <div className={styles.accuracyBar}>
                            <AccuracyProgressBar
                                accuracy={overallStats.accuracy}
                                className={styles.progressBarCustom}
                            />
                        </div>
                    </div>
                    <div className={styles.april142025}>{overallStats.date}</div>
                    <div className={styles.questionCount}>{overallStats.totalQuestions}</div>
                </div>
                <div className={styles.buttonGroup}>
                    <button
                        className={styles.buttonMorePractice}
                        onClick={handleFullTestClick}
                        disabled={isCreatingExamination}
                        style={{
                            opacity: isCreatingExamination ? 0.7 : 1,
                            cursor: isCreatingExamination ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <div className={styles.accuracy}>
                            {isCreatingExamination ? 'Creating Test...' : 'Start Test'}
                        </div>
                    </button>
                    <button className={styles.buttonViewReport} onClick={handleViewReportClick}>
                        <div className={styles.accuracy}>View Report</div>
                    </button>
                </div>
            </div>

            <div className={styles.lineDiv} />

            {/* Test Sections Preview */}
            <div className={styles.sectionPreview}>
                <div className={styles.sectionPreviewTitle}>Test Includes:</div>
                <div className={styles.sectionsList}>
                    <div className={styles.sectionItem}>
                        <div className={styles.sectionIcon}>📚</div>
                        <div className={styles.sectionDetails}>
                            <div className={styles.sectionName}>Reading and Writing</div>
                            <div className={styles.sectionInfo}>64 questions • ~64 minutes</div>
                        </div>
                    </div>
                    <div className={styles.sectionItem}>
                        <div className={styles.sectionIcon}>🔢</div>
                        <div className={styles.sectionDetails}>
                            <div className={styles.sectionName}>Math</div>
                            <div className={styles.sectionInfo}>44 questions • ~70 minutes</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Test Report Modal */}
            <TestReportModal
                visible={isReportModalVisible}
                onClose={handleCloseReportModal}
                reportData={reportToShow}
            />
        </div>
    );
};

export default SATFullTestSelectorBox;